// Per-route document titles so browser tabs and history are navigable
// (previously the whole app shared one static title = the app name).
//
// usePageTitle() is mounted once in the Layout (AppShell). It resolves the
// current path to a human title via longest-prefix match, runs it through the
// tenant terminology map (Candidates → Applicants, …), and formats it as
// "<Page> · <App name>". Unmapped paths fall back to just the app name.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';

// Most-specific prefixes are matched first (the table is sorted by descending
// prefix length at module load), so '/compliance/admin' beats '/compliance'
// and '/candidates/:id' resolves through '/candidates'.
const ROUTE_TITLES: Array<[string, string]> = [
  ['/dashboard', 'Dashboard'],
  ['/my-day', 'My Day'],
  ['/management-dashboard', 'Management Dashboard'],
  ['/recruiting-dashboard', 'Recruiting Dashboard'],
  ['/hr-dashboard', 'HR Dashboard'],
  ['/ceo-dashboard', 'CEO Dashboard'],
  ['/daily-briefing', 'Daily Briefing'],
  ['/daily-summary', 'Daily Summary'],
  ['/performance', 'Performance & Scorecard'],
  ['/action-plan', 'Action Plan'],
  ['/client-health', 'Client Radar'],
  ['/ai-brain', 'Chief of Staff'],
  ['/candidates/hr-interviews', 'Interview Queue'],
  ['/candidates', 'Candidates'],
  ['/smart-search', 'Smart Search'],
  ['/match-alerts', 'Match Alerts'],
  ['/interviews', 'Interview Scheduler'],
  ['/recruiting/playbooks', 'Recruiting Call Scripts'],
  ['/jobs', 'Jobs'],
  ['/settings/job-boards', 'Job Boards'],
  ['/finance/rate-cards', 'Pay Rate Calculator'],
  ['/kanban', 'Kanban Board'],
  ['/pipeline', 'Candidate Pipeline'],
  ['/submissions', 'Submissions'],
  ['/pending-tracker', 'Candidate Updates'],
  ['/client-approvals', 'Client Approvals'],
  ['/inbound-docs', 'Inbound Docs'],
  ['/sequences', 'SMS / Email Campaign'],
  ['/clients-orgs', 'Clients'],
  ['/clients', 'Facilities'],
  ['/ats-reports', 'Recruiting Reports'],
  ['/weekly-report', 'Weekly Recruiting Report'],
  ['/benefits', 'Benefits'],
  ['/referrals', 'Referrals'],
  ['/credentialing-dashboard', 'Credentialing Dashboard'],
  ['/credentialing', 'Credentialing'],
  ['/documents', 'Document Checker'],
  ['/onboarding-packets', 'Pre-start Packets'],
  ['/onboarding-bundle', 'E-sign Bundle'],
  ['/onboarding', 'Onboarding'],
  ['/hr/offer-letters', 'Offer Letters'],
  ['/esign/documents', 'eSign Documents'],
  ['/esign', 'eSign'],
  ['/compliance/admin', 'Compliance Admin'],
  ['/compliance/joint-commission', 'Joint Commission'],
  ['/compliance/my', 'My Assignments'],
  ['/compliance/certificates', 'My Documents'],
  ['/compliance/messages', 'Messages'],
  ['/compliance', 'Compliance'],
  ['/company-certifications', 'Company Certifications'],
  ['/call-playbooks', 'Playbook Builder'],
  ['/staff', 'Staff Management'],
  ['/placements', 'Placements'],
  ['/renewals', 'Assignment Renewals'],
  ['/employee-renewals', 'Renewals & Time Off'],
  ['/attendance-policy', 'Attendance Policy'],
  ['/scheduling', 'Scheduling'],
  ['/attendance', 'Attendance'],
  ['/timekeeping', 'Timekeeping'],
  ['/timesheet-flags', 'Timesheet Flags'],
  ['/pto', 'PTO'],
  ['/incidents', 'Incidents'],
  ['/business-dev', 'Business Development'],
  ['/gov-grants', 'Grants & Federal'],
  ['/contracts', 'Contracts'],
  ['/ai-advisors', 'Advisory Board'],
  ['/board-room', 'Board Room'],
  ['/ai-team', 'Team Tasks'],
  ['/ai-missions', 'Missions'],
  ['/ai-assistant', 'AI Chat'],
  ['/ai-knowledge', 'Knowledge Base'],
  ['/templates', 'Message Templates'],
  ['/sms', 'SMS Approvals'],
  ['/email-monitor', 'Email Monitor'],
  ['/security/agent', 'Security Agent'],
  ['/security', 'Security & MFA'],
  ['/team-lifecycle', 'Team Onboard / Offboard'],
  ['/training', 'Training Hub'],
  ['/reports', 'Reports'],
  ['/reminders', 'Reminders'],
  ['/notifications', 'Notifications'],
  ['/tasks', 'Tasks'],
  ['/help', 'Help Center'],
  ['/settings', 'Settings'],
];

// Longest prefix wins — sort in place so '/compliance/admin' precedes
// '/compliance' regardless of authoring order.
ROUTE_TITLES.sort((a, b) => b[0].length - a[0].length);

export function titleForPath(pathname: string): string | null {
  for (const [prefix, title] of ROUTE_TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return title;
  }
  return null;
}

export function usePageTitle(): void {
  const { pathname } = useLocation();
  const { branding, applyTerm } = useBranding();
  useEffect(() => {
    const base = branding.app_name;
    const page = titleForPath(pathname);
    document.title = page ? `${applyTerm(page)} · ${base}` : base;
  }, [pathname, branding, applyTerm]);
}
